/**
 * The company's own Tripletex (backend/services/tripletex.js, owner 2026-09-28): the administrator
 * connects it in Innstillinger, Lønn, and Timer til lønn sends the month as a lønnsbilag. The key is
 * sent once and never read back; the server answers only whether the company is connected.
 */
import { del, errorText, get, post, put } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Panel, toast } from '@/ui';
import { num } from '@/ui/format';
import { Notice } from '@/ui/page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type CSSProperties, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeldScope, type HeldSendScope } from './HeldScope';
import { IntegrationCapability } from './IntegrationCapability';
import { invalidatePayMonth, when } from './Month';
import { StaffChanges, StoreDepartment } from './PowerOffice';
import { PowerOfficeAccounts } from './PowerOfficeAccounts';
import { PowerOfficeContracts } from './PowerOfficeContracts';
import { PowerOfficeLeaves } from './PowerOfficeLeaves';
import { PowerOfficeMatchPanel } from './PowerOfficeMatch';
import { TRIPLETEX, useLandOn } from './payrollSystem';

/** GET /admin/tripletex */
export interface TripletexStatus {
  available: boolean;
  connected: boolean;
  company_name: string | null;
  connected_at: string | null;
  /** sending to Tripletex starts off until an administrator switches it on (#296 ONB-18) */
  outbound_on?: boolean;
  outbound_at?: string | null;
  department?: { id: number; code: string | null; name: string } | null;
  sent: {
    year: number;
    month: number;
    /** null when the outcome is unknown */
    transaction_id: string | null;
    outcome?: 'sent' | 'unknown';
    payslips: number;
    lines: number;
    sent_at: string;
    /** an unknown send: what it contained against the month's lines now (PROM-23) */
    scope?: HeldSendScope;
  }[];
}
/** GET /admin/tripletex/plan */
export interface TripletexPlan {
  year: number;
  month: number;
  company_name: string | null;
  people: number;
  lines: { user_id: number; name: string; pay_type: string; count: number; rate: number | null }[];
  missing_people: {
    user_id: number;
    name: string;
    employee_number: string | null;
    /** shared_number: another person in the month has the same number (#168) */
    reason: 'no_number' | 'not_in_tripletex' | 'shared_number';
  }[];
  missing_types: { pay_type: string; code: string | null; reason: 'no_code' | 'not_in_tripletex' }[];
  already_sent: { transaction_id: string | null; outcome?: 'sent' | 'unknown'; sent_at: string } | null;
  /** false while sending is off for the store, or for any store of the push (#296 ONB-18) */
  outbound_on?: boolean;
  /** the hours this plan was made from (#265); the send carries it back and is refused when they changed */
  revision?: string;
}
/** One push of a month over several stores: the stores that share a Tripletex company (plan 0031). */
export interface TripletexGroup extends TripletexPlan {
  stores: { tenant_id: number; name: string }[];
  not_connected?: boolean;
}
/** GET /admin/tripletex/plan with `stores`: one push per Tripletex company */
export interface TripletexGroups {
  year: number;
  month: number;
  groups: TripletexGroup[];
  revision?: string;
}
/** A send Tripletex never answered (#265) is kept with outcome 'unknown': it may or may not be there. */
export const unanswered = (x: { outcome?: string } | null | undefined) => x?.outcome === 'unknown';
const groupsOf = (p: TripletexPlan | TripletexGroups): TripletexGroup[] =>
  'groups' in p ? p.groups : [{ ...p, stores: [] }];
/** a push that stops the send: anything unmatched or not connected, unless it was sent already */
const stops = (g: TripletexGroup) =>
  !g.already_sent &&
  (!!g.not_connected || g.outbound_on === false || g.missing_people.length > 0 || g.missing_types.length > 0);

export const KEY = (t?: number) => ['tripletex', t];
export function useTripletex(enabled = true) {
  const { tenant } = useTenant();
  return useQuery({
    queryKey: KEY(tenant?.id),
    queryFn: () => get<TripletexStatus>('/admin/tripletex'),
    enabled,
  });
}

/**
 * How a customer makes the key, as Tripletex works today (walked 2026-10-21 in a Tripletex trial,
 * docs/user-flows/2026-10-21-tripletex-onboarding): the API-tilgang tab exists only after the free
 * add-on «Integrasjoner» is ordered, and Timefly is not in Tripletex's application list, so the key
 * is made with «Tilpasset oppsett» and the application name typed. One list for Oppsett and
 * Innstillinger, Lønn.
 */
export function TripletexKeySteps({ style, className }: { style?: CSSProperties; className?: string }) {
  return (
    <ol style={style} className={className}>
      <li>
        {T(
          'I Tripletex: Selskap, Mitt abonnement, og bestill den gratis tilleggstjenesten Integrasjoner (en gang).',
        )}
      </li>
      <li>{T('Åpne brukeren din, fanen API-tilgang (under Mer), og trykk Ny nøkkel.')}</li>
      <li>
        {T('Kryss av for Tilpasset oppsett, velg Alle tilganger og skriv Timefly som applikasjonsnavn.')}
      </li>
      <li>{T('Trykk Opprett nøkkel, kopier den (den vises bare én gang) og lim den inn her.')}</li>
    </ol>
  );
}

const mm = (y: number, m: number) => `${String(m).padStart(2, '0')}.${y}`;

/** Innstillinger, Lønn: connect with a key made in Tripletex, or disconnect. */
export function TripletexConnect() {
  const q = useTripletex();
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const [token, setToken] = useState('');
  // Koble ansatte og lønnsarter opens by itself once connected, as for PowerOffice
  const [matching, setMatching] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [leaves, setLeaves] = useState(false);
  const [accounts, setAccounts] = useState(false);
  const done = (s: TripletexStatus) => qc.setQueryData(KEY(tenant?.id), s);
  const connect = useMutation({
    mutationFn: () => put<TripletexStatus>('/admin/tripletex', { token }),
    onSuccess: (s) => {
      done(s);
      setToken('');
      setMatching(true);
      toast(`${T('Koblet til Tripletex')}${s.company_name ? `: ${s.company_name}` : ''}`);
    },
    onError: (e) => toast(errorText(e)),
  });
  const disconnect = useMutation({
    mutationFn: () => del<TripletexStatus>('/admin/tripletex'),
    onSuccess: (s) => {
      done(s);
      toast(T('Koblet fra Tripletex'));
    },
    onError: (e) => toast(errorText(e)),
  });
  const s = q.data;
  useLandOn('tripletex', !!s);
  if (!s) return null;
  return (
    <Panel
      id="tripletex"
      title={
        <>
          {T('Tripletex')}{' '}
          <Chip tone="warn" small>
            {T('Beta')}
          </Chip>
        </>
      }
      sub={T('Send godkjente timer rett til lønn i Tripletex, i stedet for å laste opp filen.')}
    >
      {!s.available ? (
        <p style={{ margin: 0, color: 'var(--ink3)' }}>
          {T('Den direkte koblingen er ikke slått på ennå. Bruk Tripletex-filen under Rapporter så lenge.')}
        </p>
      ) : s.connected ? (
        <div style={{ display: 'grid', gap: 10 }}>
          <span>
            {T('Koblet til')} <b>{s.company_name ?? T('Tripletex')}</b>
          </span>
          <StaffChanges onAdd={() => setMatching(true)} sys={TRIPLETEX} />
          <StoreDepartment status={s} onSaved={done} sys={TRIPLETEX} />
          {/* people, pay codes and pay and contract from Tripletex, as PowerOffice has them (#187) */}
          <span style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!matching && (
              <Button tone="pri" onClick={() => setMatching(true)}>
                {T('Koble ansatte og lønnsarter')}
              </Button>
            )}
            {!comparing && (
              <Button onClick={() => setComparing(true)}>{T('Sammenlign lønn og kontrakt')}</Button>
            )}
            {!leaves && <Button onClick={() => setLeaves(true)}>{T('Permisjoner i Tripletex')}</Button>}
            {!accounts && (
              <Button onClick={() => setAccounts(true)}>{T('Kontoer for omsetning og lønn')}</Button>
            )}
            <Button onClick={() => disconnect.mutate()} disabled={disconnect.isPending}>
              {T('Koble fra')}
            </Button>
          </span>
          {matching && <PowerOfficeMatchPanel sys={TRIPLETEX} onClose={() => setMatching(false)} />}
          {comparing && <PowerOfficeContracts sys={TRIPLETEX} onClose={() => setComparing(false)} />}
          {leaves && <PowerOfficeLeaves sys={TRIPLETEX} onClose={() => setLeaves(false)} />}
          {accounts && <PowerOfficeAccounts sys={TRIPLETEX} onClose={() => setAccounts(false)} />}
          {/* directions, ownership, the sending switch and the last sends (#296 ONB-18, ONB-19) */}
          <IntegrationCapability sys={TRIPLETEX} name="Tripletex" status={s} queryKey={KEY(tenant?.id)} />
        </div>
      ) : (
        <form
          style={{ display: 'grid', gap: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            connect.mutate();
          }}
        >
          <TripletexKeySteps style={{ margin: 0, paddingLeft: 18, color: 'var(--ink2)', fontSize: 14 }} />
          <input
            className="inp"
            type="password"
            autoComplete="off"
            aria-label={T('Nøkkel fra Tripletex')}
            placeholder={T('Nøkkel fra Tripletex')}
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <span>
            <Button tone="pri" type="submit" disabled={connect.isPending || token.trim().length < 20}>
              {connect.isPending ? T('Kobler til…') : T('Koble til')}
            </Button>
          </span>
        </form>
      )}
    </Panel>
  );
}

/** One push in the plan: what goes, to which company, and what stops it. */
function GroupPlan({ g, multi }: { g: TripletexGroup; multi: boolean }) {
  const where = g.stores.map((x) => x.name).join(', ');
  return (
    <>
      <span>
        {multi ? `${where}: ` : `${mm(g.year, g.month)}: `}
        {g.not_connected
          ? T('ikke koblet til Tripletex. Koble til under Innstillinger, Lønn i den butikken.')
          : `${g.people} ${g.people === 1 ? T('ansatt') : T('ansatte')}, ${g.lines.length} ${g.lines.length === 1 ? T('linje') : T('linjer')}${g.company_name ? ` ${T('til')} ${g.company_name}` : ''}.`}
      </span>
      {!g.already_sent && g.outbound_on === false && (
        <Notice tone="warn" title={T('Sending til Tripletex er slått av.')}>
          <span data-testid="tripletex-outbound-off">
            {T('En administrator slår den på under Innstillinger, Lønn. Filen kan fortsatt lastes ned.')}
          </span>
        </Notice>
      )}
      {unanswered(g.already_sent) && g.already_sent && (
        <Notice tone="warn" title={T('Vi vet ikke om sendingen kom fram.')}>
          {T(
            'Tripletex svarte ikke da måneden ble sendt {when}. Sjekk under Lønn i Tripletex om lønnsbilaget finnes før du sender på nytt.',
            { when: when(g.already_sent.sent_at) },
          )}
        </Notice>
      )}
      {g.already_sent && !unanswered(g.already_sent) && (
        <Notice tone="warn" title={T('Denne måneden er allerede sendt til Tripletex.')}>
          {T('Kvittering {id}, sendt {when}. En ny fil kan fortsatt lastes ned.', {
            id: g.already_sent.transaction_id ?? '',
            when: when(g.already_sent.sent_at),
          })}
        </Notice>
      )}
      {g.missing_people.some((p) => p.reason === 'shared_number') && (
        <Notice tone="dgr" title={T('Samme ansattnummer på flere ansatte')}>
          {g.missing_people
            .filter((p) => p.reason === 'shared_number')
            .map((p) => `${p.name} (${T('ansattnr')} ${p.employee_number})`)
            .join(', ')}
          . {T('Tripletex kan ikke skille dem. Gi hver sitt nummer på ansattkortet.')}
        </Notice>
      )}
      {g.missing_people.some((p) => p.reason !== 'shared_number') && (
        <Notice tone="dgr" title={T('Finnes ikke i Tripletex')}>
          {g.missing_people
            .filter((p) => p.reason !== 'shared_number')
            .map(
              (p) =>
                `${p.name} (${p.employee_number ? `${T('ansattnr')} ${p.employee_number}` : T('mangler ansattnummer')})`,
            )
            .join(', ')}
          . {T('Ansattnummeret i Timefly må være det samme som i Tripletex.')}
        </Notice>
      )}
      {g.missing_types.length > 0 && (
        <Notice tone="dgr" title={T('Lønnsarter som ikke finnes i Tripletex')}>
          {g.missing_types.map((t) => `${t.pay_type} (${t.code ?? T('mangler lønnsartnummer')})`).join(', ')}.{' '}
          {T('Rett nummeret under Innstillinger, Lønn.')}
        </Notice>
      )}
    </>
  );
}

/** GET /admin/tripletex/reconcile: one employee and lønnsart where Tripletex differs from what was sent */
interface Figure {
  count: number;
  rate: number | null;
  amount: number | null;
}
export interface TripletexDiff {
  employee_number: string;
  name: string | null;
  code: string;
  type_name: string | null;
  change: 'added' | 'removed' | 'changed';
  fields?: ('count' | 'rate' | 'amount')[];
  sent: Figure | null;
  tripletex: Figure | null;
  /** a push compared whole: the stores the person was sent from */
  stores?: string[];
  /** a payslip in Tripletex for nobody any of the stores sent */
  not_from_timefly?: boolean;
}
export interface TripletexReconcile {
  transaction_id: string;
  relinked: boolean;
  deleted: boolean;
  unknown: boolean;
  shared: boolean;
  /** a push for several stores compared on every store's share; else `others_left_out` */
  whole?: boolean;
  others_left_out?: boolean;
  stores?: string[];
  differences: TripletexDiff[];
}
const fig = (f: Figure) =>
  f.rate != null
    ? `${num(f.count)} × ${num(f.rate)} = ${num(f.amount ?? f.rate * f.count)} kr`
    : `${num(f.count)}`;
const FIELD = { count: 'antall', rate: 'sats', amount: 'beløp' } as const;
function diffText(d: TripletexDiff) {
  const where = d.not_from_timefly
    ? `, ${T('ikke fra Timefly')}`
    : d.stores?.length
      ? `, ${d.stores.join(', ')}`
      : '';
  const who = `${d.name || `${T('ansattnr')} ${d.employee_number}`}, ${d.type_name ? `${d.type_name} (${d.code})` : d.code}${where}`;
  if (d.change === 'added' && d.tripletex) return `${who}: ${T('lagt til i Tripletex')}, ${fig(d.tripletex)}`;
  if (d.change === 'removed' && d.sent)
    return `${who}: ${T('fjernet i Tripletex')}, ${T('sendt')} ${fig(d.sent)}`;
  const parts = (d.fields ?? []).map((k) => {
    const a = d.sent?.[k];
    const b = d.tripletex?.[k];
    return `${T(FIELD[k])} ${a == null ? T('ingen') : num(a)} → ${b == null ? T('ingen') : num(b)}`;
  });
  return `${who}: ${parts.join(', ')}`;
}

/**
 * What Tripletex holds now for a sent month against what Timefly sent (#187): asked on a press, read
 * only. Changes made in Tripletex after the send show here; nothing is changed in either system.
 */
function Reconcile({ from, to }: { from: string; to: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['tripletex', 'reconcile', from, to],
    queryFn: () =>
      get<{ reconcile: TripletexReconcile | null }>(
        `/admin/tripletex/reconcile?start_date=${from}&end_date=${to}`,
      ),
    enabled: open,
  });
  if (!open)
    return (
      <button type="button" className="lnk" onClick={() => setOpen(true)}>
        {T('Sammenlign med Tripletex')}
      </button>
    );
  const r = q.data?.reconcile;
  return (
    <span data-testid="tripletex-reconcile" style={{ display: 'grid', gap: 4 }}>
      {q.isPending ? (
        T('Henter fra Tripletex …')
      ) : q.isError ? (
        errorText(q.error)
      ) : !r ? (
        T('Denne måneden er ikke sendt til Tripletex.')
      ) : r.deleted ? (
        T('Lønnsbilaget er slettet i Tripletex.')
      ) : r.unknown ? (
        r.relinked ? (
          T('Tripletex er koblet til på nytt etter at måneden ble sendt, så Timefly sammenligner ikke.')
        ) : (
          T('Timefly har ikke lagret hva som ble sendt i denne sendingen, så det kan ikke sammenlignes.')
        )
      ) : r.differences.length === 0 ? (
        T('Ingen endringer i Tripletex siden sendingen.')
      ) : (
        <>
          <b>{T('{n} endringer i Tripletex siden sendingen:', { n: r.differences.length })}</b>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {r.differences.map((d) => (
              <li key={`${d.employee_number}|${d.code}`}>{diffText(d)}</li>
            ))}
          </ul>
        </>
      )}
      {r?.shared && !r.unknown && !r.deleted && (
        <span className="lnote">
          {r.whole
            ? T('Sendingen gjaldt flere butikker: {stores}. Hele lønnsbilaget er sammenlignet.', {
                stores: (r.stores ?? []).join(', '),
              })
            : T(
                'Sendingen gjaldt flere butikker. Bare denne butikkens ansatte er sammenlignet, de andre er utelatt. Med tilgang til lønn i alle butikkene sammenlignes hele lønnsbilaget.',
              )}
        </span>
      )}
    </span>
  );
}

/**
 * Timer til lønn: see what the month becomes in Tripletex, then send it once. Over several stores
 * (`stores`) it is one push per Tripletex company, each with that company's people (plan 0031 slice
 * 2); with a subset of people `scopeNote` says why it is off and the files are the way.
 */
export function TripletexSend({
  from,
  to,
  allowed,
  scopeNote,
  stores = null,
}: {
  from: string;
  to: string;
  allowed: boolean;
  scopeNote?: string | null;
  stores?: number[] | null;
}) {
  const q = useTripletex(allowed);
  const qc = useQueryClient();
  const { tenant } = useTenant();
  const [plan, setPlan] = useState<TripletexPlan | TripletexGroups | null>(null);
  const [busy, setBusy] = useState(false);
  const s = q.data;
  if (!allowed || !s?.available) return null;
  const [y, m] = from.split('-').map(Number);
  const sent = s.sent.find((x) => x.year === y && x.month === m);
  const look = async () => {
    setBusy(true);
    try {
      setPlan(
        await get<TripletexPlan | TripletexGroups>(
          `/admin/tripletex/plan?start_date=${from}&end_date=${to}${stores ? `&stores=${stores.join(',')}` : ''}`,
        ),
      );
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await post<{ transaction_id: number; payslips: number; lines: number }>(
        '/admin/tripletex/send',
        {
          start_date: from,
          end_date: to,
          ...(stores ? { stores: stores.join(',') } : {}),
          revision: plan?.revision,
        },
      );
      toast(
        `${T('Sendt til Tripletex')}: ${r.payslips} ${T('lønnsslipper')}. ${T('Åpne Lønn i Tripletex for å se over og kjøre lønnen.')}`,
      );
      setPlan(null);
      qc.invalidateQueries({ queryKey: KEY(tenant?.id) });
      invalidatePayMonth(qc);
    } catch (e) {
      toast(errorText(e));
      // a send with no answer is now recorded (#265): the receipt line and the plan both say so
      qc.invalidateQueries({ queryKey: KEY(tenant?.id) });
      await look();
    } finally {
      setBusy(false);
    }
  };
  // notArrived: the manager looked in Tripletex after a send it never answered, and nothing is there (#265)
  const again = async (notArrived = false) => {
    setBusy(true);
    try {
      await post('/admin/tripletex/release', {
        start_date: from,
        end_date: to,
        ...(notArrived ? { not_arrived: true } : {}),
      });
      toast(
        notArrived
          ? T('Måneden kan sendes på nytt.')
          : T('Lønnsbilaget er slettet i Tripletex. Måneden kan sendes på nytt.'),
      );
      qc.invalidateQueries({ queryKey: KEY(tenant?.id) });
      await look();
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const groups = plan ? groupsOf(plan) : [];
  // nothing goes while any push is stopped; one already sent is left out of the press
  const blocked =
    !!plan && (groups.some(stops) || !groups.some((g) => !g.already_sent && g.lines.length > 0));
  // over several stores the active one need not be connected itself: each push says so
  const canLook = s.connected || !!stores;
  return (
    <div className="lexp" data-testid="export-tripletex-api">
      <div className="lexpt">
        <b>
          {T('Send til Tripletex')}{' '}
          <Chip tone="warn" small>
            {T('Beta')}
          </Chip>
        </b>
        <span>
          {s.connected
            ? T(
                'Lager et lønnsbilag i Tripletex med de samme linjene som filen. Du kjører lønnen i Tripletex som før.',
              )
            : T('Ikke koblet til ennå.')}{' '}
          {!s.connected && <Link to="/innstillinger/lonn">{T('Koble til under Innstillinger, Lønn')}</Link>}
        </span>
        {unanswered(sent) && sent && (
          <span data-testid="tripletex-unknown">
            {T(
              'Sendt {when}, men Tripletex svarte ikke. Vi vet ikke om lønnsbilaget ble laget. Sjekk under Lønn i Tripletex før du gjør noe mer.',
              { when: when(sent.sent_at) },
            )}{' '}
            <button type="button" className="lnk" disabled={busy} onClick={() => again(true)}>
              {T('Kom ikke fram, send på nytt')}
            </button>
          </span>
        )}
        {unanswered(sent) && <HeldScope scope={sent?.scope} />}
        {sent && !unanswered(sent) && (
          <span data-testid="tripletex-receipt">
            {T('Sendt {when}, kvittering {id}, {n} lønnsslipper.', {
              when: when(sent.sent_at),
              id: sent.transaction_id ?? '',
              n: sent.payslips,
            })}{' '}
            {/* once the lønnsbilag is deleted in Tripletex the month can go again (#187) */}
            <button type="button" className="lnk" disabled={busy} onClick={() => again()}>
              {T('Send på nytt')}
            </button>{' '}
            <Reconcile from={from} to={to} />
          </span>
        )}
        {canLook && scopeNote && <span className="lnote">{scopeNote}</span>}
      </div>
      {canLook && !scopeNote && (
        <span className="lexpa">
          <Button onClick={look} disabled={busy}>
            {busy && !plan ? T('Henter…') : T('Se hva som sendes')}
          </Button>
        </span>
      )}
      {plan && !scopeNote && (
        <div className="lplan" data-testid="tripletex-plan">
          {'groups' in plan && (
            <span>
              {mm(plan.year, plan.month)}:{' '}
              {T('{n} sendinger, én per firma i Tripletex.', { n: groups.length })}
            </span>
          )}
          {groups.map((g) => (
            <GroupPlan
              key={g.stores.map((x) => x.tenant_id).join(',') || 'one'}
              g={g}
              multi={'groups' in plan}
            />
          ))}
          {(() => {
            // said beside the button, not only at the top of the page (run B, 2026-10-22): a line with
            // no rate goes without Sats, and Tripletex prices it (services/tripletex.js, CV4)
            const unpriced = [
              ...new Set(
                groups
                  .filter((g) => !g.already_sent)
                  .flatMap((g) => g.lines.filter((l) => l.rate == null).map((l) => l.name)),
              ),
            ];
            return unpriced.length > 0 ? (
              <span className="lnote" data-testid="tripletex-unpriced">
                {T(
                  'Uten timelønn i Timefly: {names}. Linjene sendes uten sats, og Tripletex bruker satsen som står der.',
                  {
                    names: unpriced.join(', '),
                  },
                )}
              </span>
            ) : null;
          })()}
          <span>
            <Button tone="pri" onClick={send} disabled={busy || blocked}>
              {busy ? T('Sender…') : T('Send til Tripletex')}
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
